import React, { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InternalApiActiveAlertNoteTemplate, InternalApiAlertType } from '../../api-internal';
import { MenuItem, SalvButton, SalvMenuButton, SalvModal, Size } from '@salvapp/ui-components';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines';
import { alertNoteTemplateInternalApi } from '../../apis';
import './AlertNoteTemplateDropdown.scss';

interface AlertNoteTemplateDropdownProps {
  alertType: InternalApiAlertType;
  targetElement: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
  onTemplateSelect: (template: InternalApiActiveAlertNoteTemplate) => void;
  className?: string;
  disabled?: boolean;
}

const AlertNoteTemplateDropdown: React.FC<AlertNoteTemplateDropdownProps> = ({
  alertType,
  targetElement,
  onTemplateSelect,
  className,
  disabled = false,
}) => {
  const [confirmTemplate, setConfirmTemplate] = useState<InternalApiActiveAlertNoteTemplate | null>(null);

  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['activeAlertNoteTemplates', alertType],
    queryFn: () =>
      alertNoteTemplateInternalApi.findUsableAlertNoteTemplates({
        alertType,
      }),
  });

  const handleTemplateSelect = useCallback(
    (template: InternalApiActiveAlertNoteTemplate) => {
      const target = targetElement.current;
      if (!target) return;

      const hasExistingContent = target.value.trim().length > 0;

      if (hasExistingContent) {
        setConfirmTemplate(template);
      } else {
        onTemplateSelect(template);
      }
    },
    [targetElement, onTemplateSelect]
  );

  const handleConfirmReplace = useCallback(() => {
    if (confirmTemplate) {
      onTemplateSelect(confirmTemplate);
      setConfirmTemplate(null);
    }
  }, [confirmTemplate, onTemplateSelect]);

  const handleAction = useCallback(
    (selector: (string | undefined)[]) => {
      const templateHandle = selector[0];
      const template = templates?.find((t) => t.handle === templateHandle);
      if (template) {
        handleTemplateSelect(template);
      }
    },
    [templates, handleTemplateSelect]
  );

  if (error) {
    return (
      <div className={`alert-note-template-dropdown ${className || ''}`}>
        <div className='alert alert-danger'>Failed to load templates</div>
      </div>
    );
  }

  return (
    <>
      <SalvMenuButton
        size={Size.SMALL}
        variant='muted'
        onAction={handleAction}
        icon={faFileLines}
        enableFilter
        filterPlaceholder='Search templates...'
        disabled={disabled || isLoading}
        className={className}
        ariaLabel={isLoading ? 'Loading templates...' : 'Insert note template'}
      >
        {templates && templates.length > 0 ? (
          templates.map((template) => <MenuItem key={template.handle} title={template.name} value={template.handle} />)
        ) : (
          <MenuItem key='empty' title={isLoading ? 'Loading...' : 'No templates available'} value='' />
        )}
      </SalvMenuButton>

      <SalvModal show={!!confirmTemplate} onHide={() => setConfirmTemplate(null)}>
        <SalvModal.Header>Replace existing content?</SalvModal.Header>

        <SalvModal.Body>
          <div className='alert-note-template-confirm'>
            <p>The note field already contains content. Do you want to replace it with this template?</p>
            {confirmTemplate && (
              <div className='template-preview'>
                <h4>{confirmTemplate.name}</h4>
                <pre className='template-content'>{confirmTemplate.content}</pre>
              </div>
            )}
          </div>
        </SalvModal.Body>

        <SalvModal.Footer>
          <SalvButton label='Cancel' variant='muted' onClick={() => setConfirmTemplate(null)} />
          <SalvButton label='Replace' variant='primary' onClick={handleConfirmReplace} />
        </SalvModal.Footer>
      </SalvModal>
    </>
  );
};

export default AlertNoteTemplateDropdown;
